import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  CardMedia, 
  IconButton
} from '@mui/material';
import styled from '@emotion/styled';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

// Styled components
const TeamSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#111111',
  padding: '100px 0',
  position: 'relative',
  backgroundImage: 'url("/assets/images/bg/team-bg.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(17, 17, 17, 0.85)', // Dark overlay for better text readability
    zIndex: 1
  },
  [theme.breakpoints.down('md')]: {
    padding: '70px 0',
  }
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '60px',
  [theme.breakpoints.down('md')]: {
    marginBottom: '40px',
    flexDirection: 'column',
    alignItems: 'flex-start',
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  position: 'relative',
  paddingBottom: '20px',
  fontWeight: 700,
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '-20px',
    height: '3px',
    width: '50px',
    backgroundColor: theme.palette.primary.main,
  }
}));

const ViewButton = styled('a')(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  padding: '10px 20px',
  textDecoration: 'none',
  fontWeight: 600,
  display: 'inline-block',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

const TeamCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'transparent',
  height: '100%',
  position: 'relative',
  '&:hover .linkedin-icon': {
    opacity: 1,
    visibility: 'visible',
  }
}));

const TeamImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  marginBottom: '20px',
  height: '450px',
  backgroundColor: '#111111',
}));

const TeamImage = styled(CardMedia)(({ theme }) => ({
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'top center',
  backgroundPosition: 'top center',
  transition: 'all 0.3s ease',
}));

const LinkedInButton = styled('a')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: '-80px', // Start off-screen to the right
  backgroundColor: theme.palette.primary.main,
  color: '#ffffff',
  width: '80px',
  height: '80px',
  clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'flex-end',
  paddingTop: '10px',
  paddingRight: '10px',
  zIndex: 2,
  transition: 'right 0.2s ease-in, opacity 0.2s ease-in', // Add transition for appearing
  opacity: 0, // Start hidden
  '.team-card:hover &': {
    right: 0, // Move to visible position
    opacity: 1, // Make fully visible
  },
  '.team-card:not(:hover) &': {
    transition: 'right 0.1s ease-out, opacity 0.1s ease-out', // Faster transition out
  },
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  }
}));

const TeamContent = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  padding: 0,
}));

const TeamName = styled(Typography)(({ theme }) => ({
  color: '#ffffff',
  marginBottom: '5px',
  fontWeight: 600,
  fontSize: '1.5rem',
}));

const TeamPosition = styled(Typography)(({ theme }) => ({
  color: '#888888',
  textTransform: 'uppercase',
  fontWeight: 500,
  fontSize: '0.9rem',
  letterSpacing: '1px',
}));

function Team() {
  const teamMembers = [
    {
      id: 1,
      image: '/assets/images/team/1.webp',
      name: 'Dario Vanin',
      position: 'Head of Cybersecurity',
      linkedin: 'https://www.linkedin.com/in/vdario'
    },
    {
      id: 2,
      image: '/assets/images/team/2.webp',
      name: 'Jessie Ye',
      position: 'Head of Marketing',
      linkedin: 'https://www.linkedin.com/in/jessie-ye-sz'
    },
    {
      id: 3,
      image: '/assets/images/team/4.webp',
      name: 'Luca Versiglia',
      position: 'Cloud Architect',
      linkedin: 'https://www.linkedin.com/in/luca-versiglia'
    }
  ];

  return (
    <TeamSection>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <SectionTitle>
          <Box>
            <Title variant="h2">
              We're experienced professionals
            </Title>
          </Box>
          <ViewButton href="https://hkmsp.com/team.html">
            THE TEAM
          </ViewButton>
        </SectionTitle>

        <Grid container spacing={4}>
          {teamMembers.map((member) => (
            <Grid item xs={12} sm={6} md={4} key={member.id}>
              <TeamCard className="team-card">
                <TeamImageContainer>
                  <TeamImage
                    image={member.image}
                    title={member.name}
                    component="a"
                    href={member.link}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundSize: 'cover',
                      backgroundPosition: 'top center'
                    }}
                  />
                  <LinkedInButton 
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkedInIcon sx={{ fontSize: '1.2rem' }} />
                  </LinkedInButton>
                </TeamImageContainer>
                <TeamContent>
                  <TeamName variant="h5">
                    {member.name}
                  </TeamName>
                  <TeamPosition variant="body2">
                    {member.position}
                  </TeamPosition>
                </TeamContent>
              </TeamCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </TeamSection>
  );
}

export default Team;
